import { BufferGeometry, CurveType, Vector3 } from 'three';

import {
  computeContours,
  computeContoursHoles,
  computeEdges,
  NumberArrayLike,
} from '../../utils/index.js';

import { SurfacePathCurve } from './SurfacePathCurve.js';

/**
 * Calculates the smoth path around a surface represented by a set of positions and indexes.
 * This is useful for creating a camera animation that follows the surface of a 3D model.
 */
export class SurfacePathTravel {
  /**
   * Paths of the surface.
   */
  curves: SurfacePathCurve[] = [];

  /**
   * Contours of the surface.
   */
  contours: Vector3[][] = [];

  /**
   * Return the length of the surface.
   */
  getLength() {
    return this.curves.reduce((acc, curve) => acc + curve.getLength(), 0);
  }

  /**
   * Return the points of the surface at a given position.
   */
  getPointsAt(u: number) {
    const length = this.getLength();
    let curr = 0;
    const steps = [0, ...this.curves.map((curve) => (curr += curve.getLength() / length))];

    const curveIndex = steps.findIndex((step) => step > u);

    const curve = this.getCurve(curveIndex - 1);

    const t = (u - steps[curveIndex - 1]) / (steps[curveIndex] - steps[curveIndex - 1]);

    return curve.getPointsAt(t);
  }
  /**
   * Return the points of the surface curve at a given position.
   */
  getCurvePointsAt(curveIndex: number, t: number) {
    return this.getCurve(curveIndex).getPointsAt(t);
  }

  getCurve(curveIndex: number): SurfacePathCurve {
    return this.curves[(this.curves.length + curveIndex) % this.curves.length];
  }

  getCurveCount(): number {
    return this.curves.length;
  }

  getCurves(): SurfacePathCurve[] {
    return this.curves;
  }

  setFromPositions(
    positions: NumberArrayLike,
    indexes?: NumberArrayLike,
    thresholdAngle = 0.5,
    curveType?: CurveType,
    tension?: number,
    offset?: number
  ): this {
    const edges = computeEdges(positions, indexes, thresholdAngle);
    const contours = computeContours(edges);
    const contoursHoles = computeContoursHoles(contours);

    this.contours = contoursHoles.map(([contour]) => contour);
    this.curves = this.contours.map(
      (contour) => new SurfacePathCurve(contour, curveType, tension, offset)
    );

    return this;
  }

  setFromPoints(
    points: Vector3[],
    thresholdAngle?: number,
    curveType?: CurveType,
    tension?: number,
    offset?: number
  ): this {
    const positions = points.flatMap((point) => [point.x, point.y, point.z]);
    this.setFromPositions(positions, undefined, thresholdAngle, curveType, tension, offset);

    return this;
  }

  setFromGeometry(
    geometry: BufferGeometry,
    thresholdAngle?: number,
    curveType?: CurveType,
    tension?: number,
    offset?: number
  ): this {
    const positions = geometry.getAttribute('position')?.array ?? [];
    const indexes = geometry.getIndex()?.array;

    this.setFromPositions(positions, indexes, thresholdAngle, curveType, tension, offset);

    return this;
  }

  static fromGeometry(
    geometry: BufferGeometry,
    thresholdAngle?: number,
    curveType?: CurveType,
    tension?: number,
    offset?: number
  ): SurfacePathTravel {
    return new SurfacePathTravel().setFromGeometry(
      geometry,
      thresholdAngle,
      curveType,
      tension,
      offset
    );
  }

  static fromPositions(positions: NumberArrayLike, indexes?: NumberArrayLike): SurfacePathTravel {
    return new SurfacePathTravel().setFromPositions(positions, indexes);
  }
}
